/**
 * Club pages vocabulary (design_handoff_club_pages/README.md §Shared vocabulary) — the small
 * pieces every club screen shares: pill buttons, the two-step confirm, the switch, the round
 * check, the toast, the sheet (modal on desktop, bottom sheet on phone) and the rainbow-ring tab.
 * Numbers live here so a page never retunes them.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RING_ON } from '../surface/SurfaceStyleContext';
import { safeBottom, safePad } from '../../utils/safeArea';

export const INK = '#000';
export const MUTED = '#6B7280';
export const SUBTLE = '#9CA3AF';
export const BODY = '#374151';
export const CHIP = '#F2F2F2';
export const INSET = '#F5F5F5';
export const MAGENTA = '#FF00AA';
export const CYAN = '#00FFFF';
export const GREEN = '#16A34A';
export const YELLOW = '#FACC15';
export const RED = '#DC2626';
export const RED_TINT = '#FEE2E2';
export const PINNED = '#FFF8EA';
export const RULE = '2px solid #F2F2F2';
export const TINTS = ['#A8D8EA', '#FFCCE6', '#C6B8FF', '#FAE1B4', '#B9F6CA'];

export const label = (size, tracking = '.14em', weight = 700) => ({ fontSize: size, fontWeight: weight, letterSpacing: tracking, textTransform: 'uppercase' });
export const panelTitle = { fontSize: 12, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' };
export const metaRight = { fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: SUBTLE, textTransform: 'uppercase' };
export const inputStyle = { height: 48, padding: '0 20px', border: 'none', borderRadius: 999, background: INSET, fontFamily: 'inherit', fontSize: 14, color: INK, outline: 'none', width: '100%', boxSizing: 'border-box' };
export const textareaStyle = { padding: '16px 20px', border: 'none', borderRadius: 20, background: INSET, fontFamily: 'inherit', fontSize: 14, lineHeight: 1.65, color: INK, outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical' };
export const voiceStyle = { fontFamily: '"Times New Roman", Times, serif', fontStyle: 'italic', fontSize: 18 };

/** `background` for the rainbow ring on a black fill — pair with `border: 2px solid transparent`. */
export const ringOn = RING_ON;

/** Panel: white, radius 24 (22 phone), padding 22/24 (18/16 phone), inner gap 14. */
export function ClubPanel({ children, phone = false, style, bg = '#fff', fg = INK, className = '', ...rest }) {
  return (
    <section className={`flex min-w-0 flex-col font-mono ${className}`} style={{ background: bg, color: fg, borderRadius: phone ? 22 : 24, padding: phone ? '18px 16px' : '22px 24px', gap: 14, ...style }} {...rest}>
      {children}
    </section>
  );
}

export function PanelHead({ title, right, rightColor = SUBTLE, children }) {
  return (
    <span className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
      <span style={panelTitle}>{title}</span>
      {right != null && <span style={{ ...metaRight, color: rightColor }}>{right}</span>}
      {children}
    </span>
  );
}

/**
 * Pill button. variant: primary (black, hover magenta) · secondary (white, 2px black ring) ·
 * tertiary (#F2F2F2) · ghost (white) · danger (red text) · armed (red bg) · cyan · disabled.
 */
export function PillButton({ variant = 'primary', size = 40, fontSize, tracking = '.14em', weight = 800, padding, style, className = '', children, as: As = 'button', to, ...rest }) {
  const look = {
    primary: { background: INK, color: '#fff' },
    secondary: { background: '#fff', color: INK, boxShadow: 'inset 0 0 0 2px #000' },
    tertiary: { background: CHIP, color: INK },
    ghost: { background: '#fff', color: INK },
    danger: { background: CHIP, color: RED },
    dangerGhost: { background: '#fff', color: RED },
    armed: { background: RED, color: '#fff' },
    cyan: { background: CYAN, color: INK },
    yellow: { background: YELLOW, color: INK },
    disabled: { background: '#E5E7EB', color: SUBTLE, cursor: 'default' },
    ring: { background: RING_ON, color: '#fff', border: '2px solid transparent' },
  }[variant] || {};
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: size, padding: padding ?? (size >= 48 ? '0 24px' : size >= 38 ? '0 16px' : '0 12px'),
    border: 'none', borderRadius: 999, fontFamily: 'inherit', fontSize: fontSize ?? (size >= 48 ? 12 : size >= 36 ? 11 : size >= 32 ? 10 : 9), fontWeight: weight, letterSpacing: tracking, textTransform: 'uppercase',
    cursor: 'pointer', whiteSpace: 'nowrap', textDecoration: 'none', transition: 'background-color .2s, color .2s', boxSizing: 'border-box', ...look, ...style,
  };
  const cls = `font-mono ${variant === 'primary' ? 'hover:bg-[#FF00AA]' : variant === 'ghost' || variant === 'tertiary' ? 'hover:bg-black hover:text-white' : ''} ${className}`;
  if (to) return <Link to={to} className={cls} style={base} {...rest}>{children}</Link>;
  return <As type={As === 'button' ? 'button' : undefined} className={cls} style={base} {...rest}>{children}</As>;
}

/** The ← back pill at the top-left of every club screen. */
export function ClubBackPill({ to, onClick, children, size = 40 }) {
  const inner = (
    <>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
      <span className="truncate">{children}</span>
    </>
  );
  const style = { display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%', height: size, padding: '0 16px 0 12px', borderRadius: 999, background: '#fff', color: INK, fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', textDecoration: 'none', border: 'none', fontFamily: 'inherit', cursor: 'pointer', transition: 'background-color .2s, color .2s' };
  const cls = 'font-mono hover:bg-black hover:text-white';
  if (to) return <Link to={to} onClick={onClick} className={cls} style={style} data-testid="back-pill">{inner}</Link>;
  return <button type="button" onClick={onClick} className={cls} style={style} data-testid="back-pill">{inner}</button>;
}

/** Two-step confirm: first tap arms (label becomes the question), second acts; the arm lapses after `ms`. */
export function useArmed(ms = 4000) {
  const [armed, setArmed] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const arm = useCallback((key) => {
    setArmed(key);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setArmed(null), ms);
  }, [ms]);
  const disarm = useCallback(() => { window.clearTimeout(timer.current); setArmed(null); }, []);
  /** Returns true when the action should run (second tap); arms and returns false on the first. */
  const confirm = useCallback((key) => {
    if (armed === key) { disarm(); return true; }
    arm(key);
    return false;
  }, [armed, arm, disarm]);
  return { armed, isArmed: (key) => armed === key, arm, disarm, confirm };
}

/** Switch: 52×30 pill, track black on / #E5E7EB off, 24px white knob. */
export function Switch({ on, onChange, disabled = false, label: aria, testId }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={aria} disabled={disabled} onClick={() => !disabled && onChange?.(!on)} data-testid={testId}
      style={{ flexShrink: 0, position: 'relative', width: 52, height: 30, padding: 0, border: 'none', borderRadius: 999, background: on ? INK : '#E5E7EB', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, transition: 'background-color .2s' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 25 : 3, width: 24, height: 24, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
    </button>
  );
}

/** A labelled switch row: title + description on the left, the switch on the right, rule above when `rule`. */
export function SwitchRowSoft({ title, desc, on, onChange, disabled = false, rule = true, testId }) {
  return (
    <div className="flex items-center" style={{ gap: 16, padding: '14px 0', borderTop: rule ? RULE : 'none' }}>
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{title}</span>
        {desc && <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>{desc}</span>}
      </span>
      <Switch on={on} onChange={onChange} disabled={disabled} label={title} testId={testId} />
    </div>
  );
}

/** Round check: 22px circle, 2px black ring, cyan fill + black tick when on. */
export function RoundCheck({ on, onChange, size = 22, label: aria, testId }) {
  return (
    <button type="button" role="checkbox" aria-checked={!!on} aria-label={aria} onClick={(e) => { e.stopPropagation(); onChange?.(!on); }} data-testid={testId}
      className="flex flex-none items-center justify-center" style={{ width: size, height: size, borderRadius: '50%', border: 'none', background: on ? CYAN : '#fff', boxShadow: 'inset 0 0 0 2px #000', cursor: 'pointer', padding: 0 }}>
      {on && <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 12 12" fill="none" stroke="#000" strokeWidth="2.2" strokeLinecap="square" aria-hidden="true"><path d="M2 6 L5 9 L10 3" /></svg>}
    </button>
  );
}

/** Rainbow-ring tab / chip. `on` → black fill with the ring; else white (or `off` colour). */
export function RingTab({ on, children, off = '#fff', height = 40, fontSize = 11, tracking = '.14em', gated = false, badge, badgeBg, badgeFg, style, role, ...rest }) {
  const aria = role === 'tab' ? { role, 'aria-selected': !!on } : role ? { role, 'aria-checked': !!on } : { 'aria-pressed': !!on };
  return (
    <button type="button" {...aria} className="flex-shrink-0 font-mono"
      style={{ display: 'flex', alignItems: 'center', gap: 8, height, padding: '0 18px', border: '2px solid transparent', borderRadius: 999, background: on ? RING_ON : off, color: on ? '#fff' : gated ? SUBTLE : INK, fontSize, fontWeight: 700, letterSpacing: tracking, textTransform: 'uppercase', cursor: 'pointer', transition: 'background-color .2s, color .2s', boxSizing: 'border-box', ...style }} {...rest}>
      {children}
      {badge != null && badge !== '' && <span style={{ minWidth: 18, height: 18, padding: '0 5px', boxSizing: 'border-box', borderRadius: 999, background: badgeBg || (on ? '#fff' : CHIP), color: badgeFg || INK, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800 }}>{badge}</span>}
      {gated && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><rect x="5" y="11" width="14" height="10" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>}
    </button>
  );
}

/** Status / role pill: 8–10px/800/.14em. */
export function TagPill({ bg = CHIP, fg = INK, ring, size = 8, padding, children, style, ...rest }) {
  return <span className="rounded-full font-mono uppercase" style={{ padding: padding ?? (size >= 10 ? '5px 12px' : size >= 9 ? '4px 10px' : '3px 8px'), background: bg, color: fg, boxShadow: ring ? `inset 0 0 0 2px ${ring}` : 'none', fontSize: size, fontWeight: 800, letterSpacing: '.14em', whiteSpace: 'nowrap', ...style }} {...rest}>{children}</span>;
}

export const EVENT_STATUS_PILL = {
  selling: ['#22C55E', '#fff'], funding: [YELLOW, INK], greenlit: ['#A3E635', INK], greenlit_processing: ['#A3E635', INK], processing_greenlight: ['#A3E635', INK],
  draft: ['#fff', INK], ended: ['#E5E7EB', MUTED], canceled: [RED, '#fff'], cancelled: [RED, '#fff'], dissolved: ['#E5E7EB', MUTED],
};
export const ROLE_PILL = { owner: ['OWNER', CYAN, INK], admin: ['ADMIN', INK, '#fff'], member: ['MEMBER', CHIP, INK] };

/** Toast: fixed bottom-centre, black pill, cyan dot, 2.4s. `useToast()` → { toast, say, node }. */
export function useToast(ms = 2400) {
  const [toast, setToast] = useState('');
  const timer = useRef(null);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const say = useCallback((text) => {
    window.clearTimeout(timer.current);
    setToast(text);
    timer.current = window.setTimeout(() => setToast(''), ms);
  }, [ms]);
  return { toast, say };
}

export function Toast({ text, phone = false, bottom }) {
  if (!text) return null;
  return (
    <div role="status" className="fixed flex items-center font-mono" style={{ left: '50%', bottom: safeBottom(bottom ?? (phone ? 100 : 32)), transform: 'translateX(-50%)', zIndex: 90, gap: 10, padding: '12px 18px', borderRadius: 999, background: INK, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', whiteSpace: 'nowrap', textTransform: 'uppercase' }} data-testid="club-toast">
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: CYAN }} />{text}
    </div>
  );
}

/** Sheet: modal on desktop, bottom sheet (radius 28 28 0 0) on phone; backdrop rgba(0,0,0,.55). */
export function Sheet({ open = true, onClose, phone = false, width = 520, title, children, testId = 'club-sheet' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} className="fixed inset-0 flex justify-center" style={{ zIndex: 80, background: 'rgba(0,0,0,.55)', alignItems: phone ? 'flex-end' : 'center', padding: phone ? 0 : 24, boxSizing: 'border-box' }} data-testid={`${testId}-backdrop`}>
      <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} className="flex flex-col bg-white font-mono" style={{ width: '100%', maxWidth: phone ? '100%' : width, maxHeight: '92vh', overflow: 'auto', borderRadius: phone ? '28px 28px 0 0' : 28, padding: phone ? safePad('24px') : 24, boxSizing: 'border-box', gap: 16 }} data-testid={testId}>
        {title && (
          <span className="flex items-center justify-between" style={{ gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase' }}>{title}</span>
            <button type="button" onClick={onClose} aria-label="Close" style={{ width: 40, height: 40, border: 'none', borderRadius: '50%', background: CHIP, fontFamily: 'inherit', fontSize: 14, cursor: 'pointer' }}>✕</button>
          </span>
        )}
        {children}
      </div>
    </div>
  );
}

/** Red banner: `COULD NOT SAVE` / `NOT YET` + a sentence. */
export function ErrorBand({ title = 'NOT YET', children, testId }) {
  return (
    <div className="flex items-center font-mono" style={{ gap: 12, padding: '14px 18px', borderRadius: 18, background: RED_TINT, color: '#991B1B' }} data-testid={testId}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>{title}</span>
      <span style={{ fontSize: 12 }}>{children}</span>
    </div>
  );
}

/** Empty box: dashed-free grey block with a faint title, a sentence and an optional black pill. */
export function EmptyBlock({ title, body, action, onAction, minHeight = 150, testId }) {
  return (
    <div className="flex flex-col items-center justify-center text-center font-mono" style={{ gap: 8, minHeight, padding: 24, borderRadius: 20, background: INSET }} data-testid={testId}>
      <span style={{ ...label(11, '.16em'), color: SUBTLE }}>{title}</span>
      {body && <span style={{ maxWidth: 360, fontSize: 12, lineHeight: 1.6, color: MUTED }}>{body}</span>}
      {action && <PillButton size={36} onClick={onAction} style={{ marginTop: 6 }}>{action}</PillButton>}
    </div>
  );
}

/** Segmented control: a grey track with pill options, black for the active one (or the ring when `ring`). */
export function SegmentedPills({ options, value, onChange, height = 40, fontSize = 10, ring = false, disabledKeys = [], testId }) {
  return (
    <span role="radiogroup" className="inline-flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: INSET }} data-testid={testId}>
      {options.map(([k, l]) => {
        const on = k === value;
        const dis = disabledKeys.includes(k);
        return (
          <button key={k} type="button" role="radio" aria-checked={on} disabled={dis} onClick={() => !dis && onChange(k)}
            style={{ height, padding: '0 16px', border: '2px solid transparent', borderRadius: 999, background: on ? (ring ? RING_ON : INK) : 'transparent', color: on ? '#fff' : dis ? SUBTLE : INK, fontFamily: 'inherit', fontSize, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.6 : 1 }}>
            {l}
          </button>
        );
      })}
    </span>
  );
}

/** Round avatar or a tinted initial. */
export function Face({ src, name = '', size = 40, tintKey, style }) {
  const tint = TINTS[(String(tintKey || name).length) % TINTS.length];
  return (
    <span className="flex flex-none items-center justify-center overflow-hidden rounded-full" style={{ width: size, height: size, background: src ? '#E5E7EB' : tint, fontSize: Math.round(size * 0.4), fontWeight: 800, color: INK, ...style }}>
      {src ? <img src={src} alt="" referrerPolicy="no-referrer" className="block h-full w-full object-cover" /> : (name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}
